import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { Logo } from "../components/AppShell";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";

const FloatInput = ({ label, testid, ...p }) => (
  <label className="block rounded-xl border border-[#DADCE3] bg-white px-3.5 pt-2.5 pb-2 focus-within:border-[#220b48] focus-within:ring-2 focus-within:ring-[#15fabc]/50 transition-colors">
    <span className="block text-sm text-[#5F6170]">{label}</span>
    <input {...p} data-testid={testid} className="w-full bg-transparent text-[#1B1D17] text-base py-1 focus:outline-none" />
  </label>
);

const DarkBtn = ({ busy, children, ...p }) => (
  <button {...p} disabled={busy || p.disabled} className="inline-flex items-center justify-center gap-2 bg-[#220b48] hover:bg-[#34146b] text-white font-medium rounded-xl px-8 py-3 min-w-[220px] shadow-[0_6px_16px_-6px_rgba(34,11,72,.6)] transition-[background-color,transform] active:scale-[0.98] disabled:opacity-50">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{children} {!busy && <ArrowRight className="h-4 w-4" />}
  </button>
);

const AuthFrame = ({ children, footer }) => (
  <div className="min-h-screen grid lg:grid-cols-[54%_46%] bg-white text-[#1B1D17]">
    <div className="flex flex-col min-h-screen">
      <div className="flex-1 flex items-center px-6 sm:px-16 lg:px-28 py-12">
        <div className="w-full max-w-[640px] rise"><Logo className="mb-14" />{children}</div>
      </div>
      <div className="border-t border-[#E6E7EC] px-6 sm:px-16 lg:px-28 py-7 text-[#5F6170]">{footer}</div>
    </div>
    <div className="hidden lg:flex items-center bg-[#220b48] text-white px-[9%]">
      <figure className="max-w-[540px]">
        <blockquote className="text-[34px] font-bold leading-[1.15] tracking-tight">Networking is not about just connecting people. It’s about connecting people with people, people with ideas, and people with opportunities.</blockquote>
        <figcaption className="mt-8 text-2xl text-white/90">— Michele Jennae</figcaption>
      </figure>
    </div>
  </div>
);

function CodeStep({ email, msg, onVerified, onBack }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const verify = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/verify-code", { email, code }); await onVerified(data); }
    catch (er) { setErr(errMsg(er)); setBusy(false); }
  };
  const resend = async () => { try { await api.post("/auth/request-code", { email }); setNote("A new code is on its way."); } catch (er) { setErr(errMsg(er)); } };
  return (
    <form onSubmit={verify}>
      <h1 className="text-4xl font-bold tracking-tight">Check your email</h1>
      <p className="text-[#8A8C99] text-xl mt-3">{msg || `We sent a 6-digit code to ${email}.`}</p>
      <div className="mt-10"><InputOTP maxLength={6} value={code} onChange={setCode} data-testid="login-code-input">
        <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-14 w-14 text-xl font-mono bg-white border-[#DADCE3]" />)}</InputOTPGroup>
      </InputOTP></div>
      {err && <p className="text-sm text-red-700 mt-4" data-testid="login-error">{err}</p>}
      {note && <p className="text-sm text-[#0B7A5C] mt-4">{note}</p>}
      <div className="flex flex-wrap items-center justify-between gap-4 mt-8 max-w-[560px]">
        <div className="flex gap-5 text-[#5F6170]">
          <button type="button" onClick={onBack} className="hover:text-[#220b48]" data-testid="login-change-email-btn">Change email</button>
          <button type="button" onClick={resend} className="hover:text-[#220b48]" data-testid="login-resend-btn">Resend code</button>
        </div>
        <DarkBtn busy={busy} disabled={code.length !== 6} data-testid="login-verify-btn">Verify</DarkBtn>
      </div>
    </form>
  );
}

export function Login() {
  const signup = useLocation().pathname === "/signup";
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", company: "", email: sp.get("email") || "" });
  const [step, setStep] = useState("form");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();
  const next = sp.get("next") || "/dashboard";
  useEffect(() => { setStep("form"); setErr(""); }, [signup]);
  const send = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/request-code", { email: f.email }); setMsg(data.message); setStep("code"); }
    catch (er) { setErr(errMsg(er)); } finally { setBusy(false); }
  };
  const onVerified = async (data) => {
    if (data.needs_onboarding && signup) {
      const [first, ...rest] = f.name.trim().split(/\s+/);
      await api.post("/auth/onboarding", { first_name: first, last_name: rest.join(" "), company: f.company.trim() });
      await refresh();
      return nav(next, { replace: true });
    }
    await refresh();
    nav(data.needs_onboarding ? `/onboarding?next=${encodeURIComponent(next)}` : next, { replace: true });
  };
  const q = sp.toString() ? `?${sp.toString()}` : "";
  const footer = signup
    ? <>Already have an account? <Link to={`/login${q}`} className="font-semibold underline text-[#4A4C5A]" data-testid="go-login-link">Log in</Link></>
    : <>Don't have an account? <Link to={`/signup${q}`} className="font-semibold underline text-[#4A4C5A]" data-testid="go-signup-link">Create one</Link></>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <AuthFrame footer={footer}>
      {step === "code" ? <CodeStep email={f.email} msg={msg} onVerified={onVerified} onBack={() => setStep("form")} /> : (
        <form onSubmit={send}>
          <h1 className="text-4xl font-bold tracking-tight">{signup ? "Create your account" : "Welcome back!"}</h1>
          <p className="text-[#8A8C99] text-xl mt-3">{signup ? "Get started with TapM in under a minute." : "Log in to your account below."}</p>
          <div className="space-y-4 mt-12">
            {signup && <FloatInput label="Name" required value={f.name} onChange={set("name")} autoComplete="name" testid="signup-name-input" />}
            {signup && <FloatInput label="Company" value={f.company} onChange={set("company")} autoComplete="organization" testid="signup-company-input" />}
            <FloatInput label="Email address" type="email" required autoFocus={!signup} value={f.email} onChange={set("email")} autoComplete="email" testid="login-email-input" />
          </div>
          {err && <p className="text-sm text-red-700 mt-4" data-testid="login-error">{err}</p>}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-6 max-w-[560px]">
            <span className="text-[#5F6170] text-sm">We'll email you a one-time code.</span>
            <DarkBtn busy={busy} data-testid={signup ? "signup-submit-btn" : "login-send-code-btn"}>{signup ? "Create account" : "Login"}</DarkBtn>
          </div>
        </form>
      )}
    </AuthFrame>
  );
}

export function Onboarding() {
  const { me, refresh } = useAuth();
  const [f, setF] = useState({ name: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const [sp] = useSearchParams();
  useEffect(() => { if (me === false) nav("/login"); if (me && !me.needs_onboarding) nav("/dashboard"); }, [me, nav]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const [first, ...rest] = f.name.trim().split(/\s+/);
    try { await api.post("/auth/onboarding", { first_name: first, last_name: rest.join(" "), company: f.company.trim() }); await refresh(); nav(sp.get("next") || "/dashboard", { replace: true }); }
    catch (er) { setErr(errMsg(er)); setBusy(false); }
  };
  return (
    <AuthFrame footer={<>Signed in as {me?.user?.email}</>}>
      <form onSubmit={submit}>
        <h1 className="text-4xl font-bold tracking-tight">Welcome to TapM</h1>
        <p className="text-[#8A8C99] text-xl mt-3">{me?.org ? `You're joining ${me.org.name}.` : "Tell us a little about you."}</p>
        <div className="space-y-4 mt-12">
          <FloatInput label="Name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="onboarding-name-input" />
          {!me?.org && <FloatInput label="Company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} testid="onboarding-company-input" />}
        </div>
        {err && <p className="text-sm text-red-700 mt-4">{err}</p>}
        <div className="flex justify-end mt-6 max-w-[560px]"><DarkBtn busy={busy} data-testid="onboarding-submit-btn">Continue</DarkBtn></div>
      </form>
    </AuthFrame>
  );
}
